<template>
    <v-form 
        v-model="valid" 
        ref="form" 
        :disabled="props.disabled || isSubmitting" 
        :loading="isLoadingModel"
    >
        <v-progress-linear v-if="isLoadingModel" color="primary" indeterminate />

        <v-row class="ma-0">
            <v-col class="pa-0" cols="12">
                <v-text-field 
                    v-model="code" 
                    label="Código do Modelo" 
                    :disabled="props.disabled 
                                || isSubmitting 
                                || isLoadingModel
                    " 
                    :readonly="props.readonly"
                    :rules="['create', 'new', 'update', 'edit'].includes(props.action)
                            ? [
                                formValidationRules.required(code),
                            ]
                            : []
                    "
                    autocomplete="off"
                    :clearable="!props.readonly 
                                && !(['get', 'read'].includes(props.action))
                    "
                />
            </v-col>
        </v-row>

        <v-row class="ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete
                    v-model="company_id"
                    :label="isLoadingCompanies
                            ? 'Obtendo lista de empresas...'
                            : 'Empresa'
                    "
                    :items="companiesList"
                    item-title="name"
                    item-value="id"
                    :disabled="props.disabled 
                                || isSubmitting 
                                || isLoadingModel 
                                || isLoadingCompanies
                    "
                    :clearable="!props.readonly
                                && !(['get', 'read'].includes(props.action))
                    "
                    :menu-icon="props.readonly || ['get', 'read'].includes(props.action)
                                ? ''
                                : undefined
                    "
                    :rules="['create', 'new', 'update', 'edit'].includes(props.action)
                            ? [
                                formValidationRules.required(company_id),
                            ]
                            : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>
        <v-row class="ma-0">
            <v-col class="pa-0" cols="12">
                <v-autocomplete
                    v-model="job_title_id"
                    :label="isLoadingJobTitles
                            ? 'Obtendo lista de funções...'
                            : 'Função'
                    "
                    :items="jobTitlesList"
                    :item-title="(title) => `${title?.code ? title.code + ' - ' : ''}${title?.description ? title.description : 'Função sem nome'}`"
                    item-value="id"
                    :disabled="props.disabled 
                                || isSubmitting 
                                || isLoadingModel 
                                || isLoadingJobTitles
                    "
                    :clearable="!props.readonly
                                && !(['get', 'read'].includes(props.action))
                    "
                    :menu-icon="props.readonly || ['get', 'read'].includes(props.action)
                                ? ''
                                : undefined
                    "
                    :rules="['create', 'new', 'update', 'edit'].includes(props.action)
                            ? [
                                formValidationRules.required(job_title_id),
                            ]
                            : []
                    "
                    autocomplete="off"
                />
            </v-col>
        </v-row>
        <v-expansion-panels
            v-model="expanded_panels"
            multiple
        >
            <v-expansion-panel
                v-for="weight in ['technical', 'personal', 'objectives']"
                :key="weight"
                :value="weight"
                :disabled="!(Object.keys(assessmentModel?.weights ?? {}).includes(weight))"
            >
                <template #title>
                    <div class="d-flex justify-space-between flex-grow-1">
                        <span class="text-h6">
                            {{ resolveExpansionPanelTitle(weight) }}
                        </span>
                        <v-chip
                            color="primary"
                            variant="tonal"
                        >
                            {{ `Peso: ${assessmentModel?.weights?.[weight] ? assessmentModel?.weights?.[weight] + '%' : 'N/A'}` }}
                        </v-chip>
                    </div>
                </template>
                <template #text>
                    <div v-if="hasIndicators(weight)">
                        <v-expansion-panels 
                            v-if="resolveCompetencyDataSourceFromWeightName(weight)"
                            multiple
                        >
                            <v-expansion-panel
                                v-for="comp in resolveCompetencyDataSourceFromWeightName(weight)"
                                :key="comp?.id"
                            >
                                <template #title>
                                    <div class="d-flex flex-column">
                                        <span class="text-subtitle-1 font-weight-bold">
                                            {{ `${comp?.name ?? comp?.title ?? "Competência sem nome"}${comp?.indicators?.length ? ` (${comp.indicators.length})` : ""}` }}
                                        </span>
                                        <span v-if="comp?.description" class="text-subtitle-2">
                                            {{ comp?.description }}
                                        </span>
                                    </div>
                                </template>
                                <template #text>
                                    <ul class="ml-8">
                                        <li
                                            v-for="indicator in comp?.indicators ?? []"
                                            :key="indicator?.id"
                                        >
                                            {{ 
                                                indicator?.description 
                                                ?? indicator?.name 
                                                ?? (indicator?.title ? `${indicator?.title}${indicator?.weight ? ' - ' + indicator.weight + "%" : ''}` : null)
                                                ?? "Indicador sem descrição" 
                                            }}
                                        </li>
                                    </ul>
                                </template>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>
                    <p v-else>{{ resolveExpansionPanelTextContents(weight) }}</p>
                </template>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-form>
</template>

<script setup>

import formValidationRules from '@/composables/formValidationRules';
import { onMounted, ref } from 'vue';
import AssessmentModel from "@/api/assessments/models/AssessmentModels.js"

const props = defineProps({
    disabled : {
        type: Boolean,
        default: false,
    },
    readonly : {
        type: Boolean,
        default: false,
    },
    action : {
        type: String,
        default: "get",
    },
    model : {
        type: Object,
        default: null,
    },
    modelId : {
        type: String,
        default: null,
    },
})


const expanded_panels = ref([]);
const companiesList = ref([]);
const jobTitlesList = ref([]);
const assessmentModel = ref(props.model ?? null);

// flags
const isSubmitting = ref(false);
const isLoadingModel = ref(false);
const isLoadingCompanies = ref(false);
const isLoadingJobTitles = ref(false);
const valid = ref(false);

// form values
const code = ref(null);
const company_id = ref(null);
const job_title_id = ref(null);

function hasIndicators(panel) {
    let count;

    switch (panel) {
        case "objectives":
            count = assessmentModel.value?.objectives?.length ?? 0;
            
            break;
            
        case "personal":
            count = assessmentModel.value?.personal_competencies?.length ?? 0;
            
            break;
            
        case "technical":
            count = assessmentModel.value?.technical_competencies?.length ?? 0;
            break;

        default:
            throw new RangeError(
                `Invalid panel type: '${panel}'!`
            );
    }

    return count > 0;
}

function resolveCompetencyDataSourceFromWeightName(weight_name) {
    let source;
    switch (weight_name) {
        case "objectives":
            source = assessmentModel.value?.objectives ?? [];
            
            break;
            
        case "personal":
            source = assessmentModel.value?.personal_competencies ?? [];
            
            break;
            
        case "technical":
            source = assessmentModel.value?.technical_competencies ?? [];
            break;

        default:
            throw new RangeError(
                `Invalid panel type: '${panel}'!`
            );
    }

    return source;
}

function resolveExpansionPanelTextContents(panel) {
    let count;
    let itemLabel;
    let associationLabel = `associad${panel == 'objectives' ? 'o' : 'a'}`
    switch (panel) {
        case "objectives":
            count = assessmentModel.value?.objectives?.length ?? 0;
            
            break;
            
        case "personal":
            count = assessmentModel.value?.personal_competencies?.length ?? 0;
            
            break;
            
        case "technical":
            count = assessmentModel.value?.technical_competencies?.length ?? 0;
            break;

        default:
            throw new RangeError(
                `Invalid panel type: '${panel}'!`
            );
    }

    let plural = count > 1;

    switch (panel) {
        case "objectives":
            itemLabel = `Objectivo${plural ? 's' : ''}`;
            break;
            
        case "personal":
            itemLabel = `Competência${plural || count == 0 ? 's' : ''} Pessoa${plural || count == 0 ? 'is' : 'l'}`
            break;
            
        case "technical":
            itemLabel = `Competência${plural || count == 0 ? 's' : ''} Técnica${plural || count == 0 ? 's' : ''}`
            break;

        default:
            throw new RangeError(
                `Invalid panel type: '${panel}'!`
            );
    }

    return count < 1
            ? `Não existem ${itemLabel} ${associationLabel} com este modelo.`
            : `Este modelo tem ${count} ${itemLabel} ${associationLabel}${plural ? 's' : ''}.`
}

function resolveExpansionPanelTitle(panel) {
    let titleText;
    let titleCount;
    switch (panel) {
        case "objectives":
            titleCount = assessmentModel.value?.objectives?.length ?? 0;
            titleText = "Objectivos" ;
            break;

        case "personal":
            titleCount = assessmentModel.value?.personal_competencies?.length ?? 0;
            titleText = "Competências Pessoais";
            break;

        case "technical":
            titleCount = assessmentModel.value?.technical_competencies?.length ?? 0;
            titleText = "Competências Técnicas";
            break;
    
        default:
            throw new RangeError(
                `Invalid panel type: '${panel}'!`
            );
    }

    return `${titleText}${titleCount ? ' (' + titleCount + ')' : ''}`
}

async function fetchAssessmentModelDetails() {

    if(!props?.model?.id && !props?.modelId) {
        return;
    }

    isLoadingModel.value = true;
    
    AssessmentModel.show(props?.model?.id ?? props?.modelId)
    .then((response) => {
        assessmentModel.value = response?.data ?? null;

        code.value = assessmentModel.value?.assessment_model?.code ?? null;
        companiesList.value = [
            {...(assessmentModel.value?.company ?? {})}
        ];
        company_id.value = assessmentModel.value?.company?.id ?? null;
        jobTitlesList.value = [
            {
                id: assessmentModel.value?.job_title?.id ?? null,
                code: assessmentModel.value?.job_title?.code ?? null,
                description: assessmentModel.value?.job_title?.description ?? null
            }
        ]
    })
    .catch((error) => {
        console.error(error);
        throw error;
    })
    .finally(() => {
        isLoadingModel.value = false;
    })
}

onMounted(() => {
    fetchAssessmentModelDetails();
})

</script>